// src/pages/MainHubPage.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { usePermissions } from '../hooks/usePermissions';
import { MODULES } from '../config/modules';
import { formatCurrency } from '../utils/formatters';
import { kpiService } from '../services/kpiService';
import { 
    CalendarIcon,
    ClockIcon,
    BanknotesIcon,
    ChartBarIcon
} from '@heroicons/react/24/solid';
import { SUPER_USER_EMAIL } from '../config/constants';
import { 
    BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';

const MainHubPage = ({ setPage }) => {
    const { user } = useAuth();
    const { name: companyName } = useCompany();
    
    // Hooks de permisos al nivel superior (Regla de Oro de React)
    const { isAdmin, canView } = usePermissions(); 
    
    // Estado ampliado con los nuevos datos para gráficos
    const [kpiData, setKpiData] = useState({
        visitsToday: 0,
        pendingQuotes: 0,
        pendingPayment: 0,
        techRanking: [],
        quoteFunnel: [],
        financialSummary: [],
        loading: true,
        error: null
    });

    // --- EFECTO: CARGAR DATOS USANDO EL SERVICIO ---
    useEffect(() => {
        let isMounted = true; 

        const loadDashboardData = async () => {
            try {
                // Llamada limpia al servicio: Toda la lógica de Firebase está encapsulada allí
                const stats = await kpiService.getDashboardStats(user);
                
                if (isMounted) {
                    setKpiData({
                        ...stats,
                        loading: false,
                        error: null
                    });
                }
            } catch (err) {
                console.error("Fallo al cargar dashboard:", err);
                if (isMounted) {
                    const hasChartPermission = isAdmin || user?.permissions?.reports;
                    setKpiData(prev => ({ 
                        ...prev, 
                        loading: false, 
                        error: hasChartPermission ? "No se pudieron cargar los indicadores." : null 
                    }));
                }
            }
        };

        if (user) {
            loadDashboardData();
        }

        // Limpieza al desmontar el componente
        return () => { isMounted = false; };
    }, [user]);

    // Custom Tooltip formatters for Recharts
    const renderCurrencyTooltip = (value) => formatCurrency(value);

    return (
        <div className="p-4 md:p-8 animate-fade-in w-full">
            {/* ENCABEZADO Y BIENVENIDA */}
            <div className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white dark:bg-gray-800 p-6 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 transition-colors">
                <div>
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-800 dark:text-gray-100">
                        Bienvenido, {user?.fullName || user?.displayName || 'Usuario'}
                    </h1>
                    <p className="text-gray-500 dark:text-gray-400 mt-1">
                        Panel de Control - {companyName || 'BantCore System'}
                    </p>
                </div>
                <div className="flex flex-col items-end">
                    <span className="px-4 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full font-bold text-xs uppercase tracking-wider mb-1">
                        {isAdmin ? 'Administrador' : 'Colaborador'}
                    </span>
                    <span className="text-xs text-gray-400 dark:text-gray-500">
                        {new Date().toLocaleDateString('es-CL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </span>
                </div>
            </div>

            {/* --- VENTANA INFORMATIVA (KPIs de Tarjetas Top) --- */}
            {kpiData.error ? (
                <div className="mb-8 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl border border-red-100 dark:border-red-800/30 flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <span>{kpiData.error}</span>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    {/* KPI 1: Visitas Hoy */}
                    {canView('reports') && (
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border-l-4 border-blue-500 flex items-center justify-between relative overflow-hidden group cursor-pointer hover:shadow-md transition" onClick={() => setPage('calendar')}>
                            <div className="z-10">
                                <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Agendas</p>
                                <p className="text-2xl font-bold text-gray-800 dark:text-gray-100 mt-1">
                                    {kpiData.loading ? <span className="animate-pulse">...</span> : kpiData.visitsToday}
                                </p>
                                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1 font-medium">Ver Menu Agendas &rarr;</p>
                            </div>
                            <div className="bg-blue-100 dark:bg-blue-900/30 p-3 rounded-full text-blue-600 dark:text-blue-400 group-hover:scale-110 transition-transform">
                                <CalendarIcon className="w-6 h-6"/>
                            </div>
                        </div>
                    )}

                    {/* KPI 2: Nuevo Informe de Visita (anteriormente Cotizaciones Pendientes) */}
                    {canView('quotes') && (
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border-l-4 border-indigo-500 flex items-center justify-between relative overflow-hidden group cursor-pointer hover:shadow-md transition" onClick={() => setPage('reports')}>
                            <div className="z-10">
                                <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Nuevo Informe de Visita</p>
                                <p className="text-2xl font-bold text-gray-800 dark:text-gray-100 mt-1">
                                    {kpiData.loading ? <span className="animate-pulse">...</span> : kpiData.pendingQuotes}
                                </p>
                                <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1 font-medium">Ir a Informes &rarr;</p>
                            </div>
                            <div className="bg-indigo-100 dark:bg-indigo-900/30 p-3 rounded-full text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform">
                                <ClockIcon className="w-6 h-6"/>
                            </div>
                        </div>
                    )}

                    {/* KPI 3: Por Cobrar */}
                    {canView('accounting') && (
                        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border-l-4 border-red-500 flex items-center justify-between relative overflow-hidden group cursor-pointer hover:shadow-md transition" onClick={() => setPage('accounting')}>
                            <div className="z-10">
                                <p className="text-xs font-bold text-gray-400 dark:text-gray-500 uppercase tracking-wider">Saldo por Cobrar</p>
                                <p className="text-2xl font-bold text-gray-800 dark:text-gray-100 mt-1">
                                    {kpiData.loading ? <span className="animate-pulse">...</span> : formatCurrency(kpiData.pendingPayment)}
                                </p>
                                <p className="text-xs text-red-600 dark:text-red-400 mt-1 font-medium">Ir a Cobranza &rarr;</p>
                            </div>
                            <div className="bg-red-100 dark:bg-red-900/30 p-3 rounded-full text-red-600 dark:text-red-400 group-hover:scale-110 transition-transform">
                                <BanknotesIcon className="w-6 h-6"/>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* --- SECCIÓN NUEVA: DASHBOARD ANALÍTICO AVANZADO --- */}
            {!kpiData.loading && !kpiData.error && (canView('reports') || canView('quotes') || canView('accounting')) && (
                <div className="mb-8">
                    <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center">
                        <ChartBarIcon className="w-6 h-6 mr-2 text-blue-500" /> Rendimiento Global (Mes Actual)
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                        
                        {/* 1. Ranking de Técnicos (Gráfico de Barras Horizontal) */}
                        {canView('reports') && (
                            <div className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 transition-colors h-72">
                                <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 mb-2">Ranking Visitas Técnicos</h3>
                                {kpiData.techRanking && kpiData.techRanking.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="85%">
                                        <BarChart data={kpiData.techRanking} layout="vertical" margin={{ top: 5, right: 20, left: 30, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} strokeOpacity={0.2} />
                                            <XAxis type="number" allowDecimals={false} style={{fontSize: '11px'}} />
                                            <YAxis dataKey="name" type="category" width={80} style={{fontSize: '11px', fontWeight: 'bold'}} />
                                            <Tooltip cursor={{fill: 'transparent'}} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                                            <Bar dataKey="value" fill="#3B82F6" radius={[0, 4, 4, 0]} name="Visitas" barSize={20} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="h-full flex items-center justify-center text-sm text-gray-400">Sin datos este mes.</div>
                                )}
                            </div>
                        )}

                        {/* 2. Embudo de Cotizaciones (Gráfico Circular) */}
                        {canView('quotes') && (
                            <div className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 transition-colors h-72">
                                <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 mb-2">Embudo Cotizaciones Totales</h3>
                                {kpiData.quoteFunnel && kpiData.quoteFunnel.some(q => q.value > 0) ? (
                                    <ResponsiveContainer width="100%" height="90%">
                                        <PieChart>
                                            <Pie
                                                data={kpiData.quoteFunnel}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={80}
                                                paddingAngle={5}
                                                dataKey="value"
                                            >
                                                {kpiData.quoteFunnel.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={entry.fill} />
                                                ))}
                                            </Pie>
                                            <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                                            <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ fontSize: '12px' }}/>
                                        </PieChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="h-full flex items-center justify-center text-sm text-gray-400">No hay cotizaciones registradas.</div>
                                )}
                            </div>
                        )}

                        {/* 3. Flujo Financiero Rápido (Gráfico de Barras) */}
                        {canView('accounting') && (
                            <div className="bg-white dark:bg-gray-800 p-5 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 transition-colors h-72">
                                <h3 className="text-sm font-bold text-gray-500 dark:text-gray-400 mb-2">Flujo de Caja (Efectivo)</h3>
                                {kpiData.financialSummary && kpiData.financialSummary.length > 0 ? (
                                    <ResponsiveContainer width="100%" height="85%">
                                        <BarChart data={kpiData.financialSummary} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
                                            <CartesianGrid strokeDasharray="3 3" vertical={false} strokeOpacity={0.2} />
                                            <XAxis dataKey="name" style={{fontSize: '11px'}} />
                                            <YAxis tickFormatter={(val) => `$${val/1000}k`} style={{fontSize: '10px'}} width={55} />
                                            <Tooltip formatter={renderCurrencyTooltip} cursor={{fill: 'transparent'}} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }} />
                                            <Legend wrapperStyle={{ fontSize: '11px' }} />
                                            <Bar dataKey="Ingresos" fill="#10B981" name="Recaudado (Caja)" radius={[4, 4, 0, 0]} barSize={40} />
                                            <Bar dataKey="Egresos" fill="#EF4444" name="Gastos Pagados" radius={[4, 4, 0, 0]} barSize={40} />
                                        </BarChart>
                                    </ResponsiveContainer>
                                ) : (
                                    <div className="h-full flex items-center justify-center text-sm text-gray-400">Sin movimientos financieros el mes actual.</div>
                                )}
                            </div>
                        )}

                    </div>
                </div>
            )}

            {/* --- GRID DE MÓDULOS DEL SISTEMA --- */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {MODULES.map((module) => {
                    // Verificación de seguridad robusta usando el hook
                    let hasAccess = canView(module.id);
                    
                    // Excepciones específicas del negocio
                    if (module.id === 'tools' && !isAdmin && !user?.permissions?.tools) hasAccess = true;
                    if (module.id === 'superadmin') hasAccess = user?.email === SUPER_USER_EMAIL;

                    if (!hasAccess) return null;

                    return (
                        <div 
                            key={module.id}
                            onClick={() => setPage(module.page)}
                            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6 hover:shadow-xl transition-all duration-300 cursor-pointer group hover:-translate-y-1 relative overflow-hidden"
                        >
                            <div className={`absolute top-0 right-0 p-3 opacity-10 group-hover:opacity-20 transition-opacity ${module.color.replace('bg-', 'text-')}`}>
                                {React.cloneElement(module.icon, { className: 'w-24 h-24' })}
                            </div>
                            
                            <div className={`${module.color} text-white w-14 h-14 rounded-lg flex items-center justify-center mb-4 shadow-md group-hover:scale-110 transition-transform duration-300`}>
                                {module.icon}
                            </div>
                            
                            <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100 mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                                {module.title}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                                {module.description}
                            </p>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default MainHubPage;